<template>
  <div class="view-lesson">
    <div
      v-if="loading || !(quiz && module)"
      class="d-flex justify-content-center p-5"
    >
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
    </div>

    <div v-else class="container-fluid px-lg-5 py-5 order-lg-1">
      <header v-if="module" class="d-flex mb-3">
        <button
          class="btn btn-back text-decoration-none p-0 m-0"
          @click="goBack"
        >
          <i class="icon-chevron-left mr-2" /> {{ module.name }}
        </button>
      </header>

      <div class="row">
        <div class="col-lg-8">
          <blocked-module
            v-if="isBlocked.status"
            :isBlocked="isBlocked"
            :quiz="quiz"
          />
          <quiz-info
            v-else
            :quiz="quiz"
            @update="dispatchQuizzes"
            :isBlocked="isBlocked.status"
          />
          <div class="pt-3" />
          <content-description
            is-quiz
            hide-finish-button
            :content="quiz"
            :finishedLesson="finishedQuiz"
            :loadingFinish="loadingFinish"
            :nextContent="nextLesson"
            :previousContent="previousLesson"
          />
        </div>
        <div class="col-lg-4">
          <b-tabs justified>
            <b-tab title="Conteúdo do módulo" active class="lesson-list-title">
              <tab-lesson-line-list :items="items" :current="quiz" />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlockedModule from "@/components/lesson/BlockedModule.vue";
import ContentDescription from "@/components/lesson/ContentDescription";
import TabLessonLineList from "@/components/lesson/TabLessonLineList";
import QuizInfo from "@/components/lesson/quiz/QuizInfo";
import { calculateTimeFromNow } from "@/functions/helpers";
import { mapState } from "vuex";

export default {
  components: {
    ContentDescription,
    TabLessonLineList,
    BlockedModule,
    QuizInfo
  },
  data() {
    return {
      module: undefined,
      moduleParent: undefined,
      loading: false,
      isSubmodule: false,
      loadingFinish: false
    };
  },
  computed: {
    ...mapState("modules", ["modules"]),
    ...mapState("lessons", ["lessons"]),
    ...mapState("quiz", ["quizes"]),
    ...mapState("user", ["user"]),
    items() {
      return [...this.lessons, ...this.quizes].sort((a, b) => a.sort - b.sort);
    },
    quiz() {
      if (!this.quizes.length) {
        return null;
      }

      const data = this.quizes.find(
        quiz => quiz.code == this.$route.params.quiz
      );

      return data;
    },
    nextLesson() {
      const index = this.items.findIndex(
        i => i.code == this.$route.params.quiz
      );

      //if there's a next content in the current module/submodule
      if (this.items[index + 1])
        return {
          ...this.items[index + 1],
          module: {
            id: this.$route.params.id
          }
        };

      // if the current lesson is inside a submodule
      if (this.moduleParent) {
        const sub_module_index = this.moduleParent.sub_modules.findIndex(
          submodule => submodule.id == this.quiz.module
        );

        // if there's a next submodule with content
        if (
          this.moduleParent.sub_modules[sub_module_index + 1] &&
          this.moduleParent.sub_modules[sub_module_index + 1].items.length
        ) {
          return {
            ...this.moduleParent.sub_modules[sub_module_index + 1].items[0],
            module: {
              id: this.moduleParent.sub_modules[sub_module_index + 1].id
            }
          };
        }
      }

      const module_index = this.moduleParent
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.quiz.module
            )
          )
        : this.modules.findIndex(module => module.id == this.quiz.module);

      //if there's a submodule with content in the current module
      if (
        !this.moduleParent &&
        this.modules[module_index].sub_modules.length &&
        this.modules[module_index].sub_modules[0].items.length
      ) {
        return {
          ...this.modules[module_index].sub_modules[0].items[0],
          module: {
            id: this.modules[module_index].sub_modules[0].id
          }
        };
      }

      let nextModule = undefined;
      let i = module_index + 1;
      while (this.modules[i]) {
        nextModule = this.modules[i];

        //if there's a next module with content
        if (nextModule && nextModule.items.length) {
          const moduleFirstContent = nextModule.items.find(
            item => item.type !== "submodule"
          );
          //if there's content in the next module (outside a submodule)
          if (moduleFirstContent) {
            return {
              ...moduleFirstContent,
              module: {
                id: nextModule.id
              }
            };
          }

          // look for content inside every submodule of the module
          let submoduleFirstContent = undefined;
          let j = 0;
          while (nextModule.sub_modules[j] && !submoduleFirstContent) {
            submoduleFirstContent = nextModule.sub_modules[j].items.find(
              item => item.type !== "submodule"
            );
            j++;
          }

          //if there's a submodule with content in the next module
          if (submoduleFirstContent) {
            return {
              ...submoduleFirstContent,
              module: {
                id: nextModule.sub_modules[0].id
              }
            };
          }
        }
        i++;
      }

      return null;
    },
    previousLesson() {
      const index = this.items.findIndex(
        i => i.code == this.$route.params.quiz
      );

      //if there's a previous content in the current module/submodule
      if (this.items[index - 1]) {
        return {
          ...this.items[index - 1],
          module: {
            id: this.$route.params.id
          }
        };
      }

      // if the current lesson is inside a submodule
      if (this.moduleParent) {
        const sub_module_index = this.moduleParent.sub_modules.findIndex(
          submodule => submodule.id == this.quiz.module
        );

        const previousSubmodule = this.moduleParent.sub_modules[
          sub_module_index - 1
        ];
        //if there's a previous submodule with content
        if (previousSubmodule && previousSubmodule.items.length) {
          return {
            ...previousSubmodule.items.at(-1),
            module: {
              id: previousSubmodule.id
            }
          };
        }

        const previousContent = this.moduleParent.items.findLast(
          item => item.type !== "submodule"
        );
        // if there's content outside the submodule, in the same module
        if (previousContent) {
          return {
            ...previousContent,
            module: {
              id: this.moduleParent.id
            }
          };
        }
      }

      const module_index = this.moduleParent
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.quiz.module
            )
          )
        : this.modules.findIndex(module => module.id == this.quiz.module);

      // look for a previous module with content
      let previousModule = undefined;
      let i = module_index - 1;
      while (this.modules[i]) {
        previousModule = this.modules[i];

        // look for content inside every submodule of the previous module
        let submoduleLastContent = undefined;
        let j = previousModule.sub_modules.length - 1;
        while (previousModule.sub_modules[j] && !submoduleLastContent) {
          submoduleLastContent = previousModule.sub_modules[j].items.at(-1);

          //if there's a submodule with content in the previous module
          if (submoduleLastContent) {
            return {
              ...submoduleLastContent,
              module: {
                id: previousModule.sub_modules[0].id
              }
            };
          }

          j--;
        }

        //if the previous module has content outside of submodules
        if (previousModule && previousModule.items.length) {
          const moduleLastContent = previousModule.items.findLast(
            item => item.type !== "submodule"
          );
          //if there's content in the previous module (outside a submodule)
          if (moduleLastContent) {
            return {
              ...moduleLastContent,
              module: {
                id: previousModule.id
              }
            };
          }
        }

        i--;
      }

      return null;
    },
    finishedQuiz() {
      return !!this.quiz?.progress?.finished_at;
    },
    isBlocked() {
      const status =
        this.moduleParent?.is_blocked.status ||
        this.module?.is_blocked.status ||
        this.quiz?.is_blocked;

      if (!status)
        return { status, days_left_for_release: 0, blocked_by: null };

      const blocked_by =
        this.moduleParent?.is_blocked.status ||
        (this.module?.is_blocked.status && !this.moduleParent)
          ? "module"
          : this.moduleParent &&
            !this.moduleParent?.is_blocked.status &&
            this.module?.is_blocked.status
          ? "submodule"
          : "quiz";

      let days_left_for_release =
        this.moduleParent?.is_blocked.days_left_to_release ||
        this.moduleParent?.is_blocked.days_left_for_release ||
        this.module?.is_blocked.days_left_to_release ||
        this.module?.is_blocked.days_left_for_release;

      if (!days_left_for_release && this.quiz.days_to_release) {
        const date_joined = this.user.my_courses.find(
          course => course.product_id == this.quiz.product_id
        ).date_joined;

        const time_joined = calculateTimeFromNow(date_joined);
        const diff = this.quiz.days_to_release - time_joined;

        days_left_for_release = diff < 1 ? Math.floor(diff) : Math.ceil(diff);
      }

      if (!days_left_for_release && this.quiz.release_at) {
        days_left_for_release = Math.ceil(
          calculateTimeFromNow(this.quiz.release_at)
        );
      }

      return { status, days_left_for_release, blocked_by };
    }
  },
  mounted() {
    this.loading = true;
    this.$store
      .dispatch("lessons/getAll", this.$route.params.id)
      .then(() => {
        this.module = this.modules.find(m => m.id == this.quiz.module);

        if (!this.module) {
          this.moduleParent = this.modules.find(m =>
            m.sub_modules.some(s => s.id === this.quiz.module)
          );
          this.module = this.moduleParent.sub_modules.find(
            s => s.id === this.quiz.module
          );
          this.isSubmodule = true;
        }
      })
      .finally(() => {
        this.loading = false;
      });

    this.dispatchQuizzes();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "module", params: { id: this.module.id } });
    },
    dispatchQuizzes() {
      this.$store
        .dispatch("quiz/getAll", {
          module_id: this.$route.params.id,
          params: { limit: 1000, status: "published" }
        })
        .then();
    }
  }
};
</script>

<style lang="scss" scoped>
.view-lesson {
  min-height: calc(100vh - 56px);

  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }

  iframe {
    height: 500px;
  }
}
</style>
